import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import L, { LatLngTuple, Map } from 'leaflet'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingFilesState,
  buildingModeState,
  listChangedPointsState,
  listLinkState,
  listPointState,
  revalidateFileListState,
  revalidatePointDetailState,
  selectedAreaState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import { currentPovState } from 'store/krpanoStore'
import useSWR from 'swr'
import { Point } from 'types/building'
import Utils from 'utils'
import Krpano from 'utils/krpano'

type LeafletRef = {
  createPolylineRef: any
  polylineRef: any
  initPositionRef: any
  mapContainerRef: Map | any
  povMarkerRef: any
  radarDragFlag: {
    isRadarMode: boolean
    isDragging: boolean
    rotate: any | null
    hgap: any | null
    mouseHoldTime: any | null
  }
}

type Props = {
  leafletRef: MutableRefObject<LeafletRef>
}

export default function useMapItem({ leafletRef }: Props) {
  const { id: buildingId, floorId } = useParams()
  const { navigate } = useDirectory()
  const { t } = useTranslation()

  const buildingMode = useRecoilValue(buildingModeState)
  const [selectedArea, setSelectedArea] = useRecoilState(selectedAreaState)
  const [buildingFiles, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [currentSelectedPoint, setCurrentSelectedPoint] =
    useRecoilState(selectedPoint)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [points, setPoints] = useRecoilState(listPointState)
  const [links, setLinks] = useRecoilState(listLinkState)
  const [listChangedPoints, setListChangedPoints] = useRecoilState(
    listChangedPointsState
  )
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)

  const { mutate: refetchPoints, isValidating: isFetchingPoint } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const { trigger: syncChangedPoint, isMutating: isSyncChangedPoint } =
    useMutation('v1/points')

  const debouncedRef = useRef<any>(null)
  const dragMarkerRef = useRef<boolean>(false)

  const { trigger: handlePoint, isMutating } = useMutation('v1/points')
  const { trigger: handleUpdateDirections } = useMutation(
    'v1/points/change-direction'
  )

  const linkEventHandler = (id: string, l_index: number) => {
    return {
      click() {
        !isMutating &&
          buildingMode?.selectedMode === 'link' &&
          leafletRef.current.polylineRef[l_index] &&
          handlePoint(
            {
              query: ['delete-link'],
              data: {
                building_id: Number(buildingId),
                floor_id: Number(floorId),
                link_id: id
              }
            },
            {
              onSuccess: () => {
                setLinks((prev) => prev.filter((item) => item.id !== id))
                if (currentSelectedPoint && id.includes(currentSelectedPoint)) {
                  setRevalidatePointDetail(currentSelectedPoint)
                }
              },
              onError: (error) => {
                Utils.handleErrorNavigate(error, navigate)
              }
            }
          )
      }
    }
  }

  const handleRedrawLink = (
    changeLink: number[],
    changePos: number[],
    changeLatLng: any
  ) => {
    const newLinks = [...links]
    for (let i = 0; i < changeLink.length; i += 1) {
      if (changePos[i] === 0) {
        const link = {
          ...newLinks[changeLink[i]],
          point1: {
            ...newLinks[changeLink[i]].point1,
            x: changeLatLng[i][0].lng,
            y: changeLatLng[i][0].lat
          }
        }
        newLinks[changeLink[i]] = link
      } else {
        const link = {
          ...newLinks[changeLink[i]],
          point2: {
            ...newLinks[changeLink[i]].point2,
            x: changeLatLng[i][1].lng,
            y: changeLatLng[i][1].lat
          }
        }
        newLinks[changeLink[i]] = link
      }
    }
    return newLinks
  }

  const handleResetMapItems = (
    changeLink: number[],
    changePos: number[],
    lat: any,
    lng: any
  ) => {
    for (let i = 0; i < changeLink.length; i += 1) {
      const latlng =
        leafletRef.current.polylineRef[changeLink[i]].ref.getLatLngs()
      if (changePos[i] === 0) {
        leafletRef.current.polylineRef[changeLink[i]].ref.setLatLngs([
          { lat, lng },
          latlng[1]
        ])
      } else {
        leafletRef.current.polylineRef[changeLink[i]].ref.setLatLngs([
          latlng[0],
          { lat, lng }
        ])
      }
    }
    setPoints((prev) => [...prev])
  }

  const markerEventHandler = (point: Point, imgBounds: LatLngTuple) => {
    const { id, x: lng, y: lat } = point
    const hgap = point.initView.radarHGap
    const changeLink: number[] = []
    const changePos: number[] = []
    const changeLatLng: any = []
    return {
      dragstart() {
        if (!dragMarkerRef.current) {
          dragMarkerRef.current = true
        }
        for (let i = 0; i < leafletRef.current.polylineRef.length; i += 1) {
          if (
            leafletRef.current.polylineRef[i].ref &&
            leafletRef.current.polylineRef[i].id.includes(point.id)
          ) {
            changeLink.push(i)
            const latlng: any =
              leafletRef.current.polylineRef[i].ref.getLatLngs()
            for (let j = 0; j < 2; j += 1) {
              if (latlng[j].lat === lat && latlng[j].lng === lng) {
                changePos.push(j)
                changeLatLng.push(latlng)
              }
            }
          }
        }
        if (debouncedRef.current) {
          clearTimeout(debouncedRef.current)
          debouncedRef.current = null
        }
      },
      drag(event: any) {
        const newlatlng = {
          lat: event.latlng.lat,
          lng: event.latlng.lng
        }
        for (let i = 0; i < changeLink.length; i += 1) {
          changeLatLng[i].splice(changePos[i], 1, newlatlng)
          leafletRef.current.polylineRef[changeLink[i]].ref.setLatLngs(
            changeLatLng[i]
          )
        }
        if (currentSelectedPoint === id && leafletRef.current.povMarkerRef) {
          leafletRef.current.povMarkerRef?.setLatLng(newlatlng)
        }
      },
      dragend(event: any) {
        if (dragMarkerRef.current) {
          setTimeout(() => {
            dragMarkerRef.current = false
          }, 500)
        }
        /* eslint no-underscore-dangle: 0 */
        const { x, y } = {
          x: event.target._latlng.lng,
          y: event.target._latlng.lat
        }
        if (x <= imgBounds[1] && y <= imgBounds[0] && x >= 0 && y >= 0) {
          // get previous point change index
          const newListPoint = [...listChangedPoints]
          const previousChange = newListPoint.findIndex(
            (item) => item.id === id
          )
          if (previousChange !== -1) {
            newListPoint.splice(previousChange, 1, { id, x, y })
          } else {
            newListPoint.push({ id, x, y })
          }
          setListChangedPoints(newListPoint)
          setPoints((prev) =>
            prev.map((item) => (item.id === id ? { ...item, x, y } : item))
          )
          const changedPointDetail = newListPoint.find(
            (item) => item.id === currentSelectedPoint
          )
          changedPointDetail &&
            setPointDetail(
              (prev) =>
                prev && {
                  ...prev,
                  x: changedPointDetail.x,
                  y: changedPointDetail.y
                }
            )
          setLinks(handleRedrawLink(changeLink, changePos, changeLatLng))
        } else {
          handleResetMapItems(changeLink, changePos, lat, lng)
          if (leafletRef.current.povMarkerRef && currentSelectedPoint === id) {
            leafletRef.current.povMarkerRef.setLatLng({ lat, lng })
          }
        }
      },
      click() {
        if (dragMarkerRef.current) {
          return
        }
        if (selectedArea && currentSelectedPoint === id) {
          Krpano.LoadChecking(() => {
            setCurrentSelectedPoint(undefined)
            if (leafletRef.current.povMarkerRef) {
              leafletRef.current.povMarkerRef = null
            }
          })
        }
        if (currentSelectedPoint !== id) {
          Krpano.LoadChecking(() => {
            !selectedArea && setSelectedArea(point.areaId)
            setCurrentSelectedPoint(id)
            id !== pointDetail?.id && setPointDetail(undefined)
          })
        }
        if (currentPov) {
          setCurrentPov(null)
        }
      },
      mousedown() {
        if (buildingMode?.selectedMode !== 'link') return
        if (!leafletRef.current.createPolylineRef) {
          leafletRef.current.mapContainerRef.dragging.disable()
          leafletRef.current.createPolylineRef = L.polyline(
            [
              [lat, lng],
              [lat, lng]
            ],
            { color: '#000', weight: 5 }
          )
          leafletRef.current.initPositionRef = {
            id,
            hgap,
            latlng: { lat, lng }
          }
          leafletRef.current.createPolylineRef.addTo(
            leafletRef.current.mapContainerRef
          )
          leafletRef.current.createPolylineRef.on('mouseup', () => {
            leafletRef.current.mapContainerRef.dragging.enable()
            leafletRef.current.mapContainerRef.removeLayer(
              leafletRef.current.createPolylineRef
            )
            leafletRef.current.initPositionRef = null
            leafletRef.current.createPolylineRef = null
          })
        }
      },
      mouseup() {
        if (buildingMode?.selectedMode !== 'link') return
        if (leafletRef.current.createPolylineRef) {
          leafletRef.current.mapContainerRef.dragging.enable()
        }

        // Check if initPositionRef exists
        if (!leafletRef.current.initPositionRef) {
          return
        }

        // check if click at the same point
        if (
          leafletRef.current.initPositionRef?.latlng.lat === lat &&
          leafletRef.current.initPositionRef?.latlng.lng === lng
        ) {
          leafletRef.current.mapContainerRef.removeLayer(
            leafletRef.current.createPolylineRef
          )
          leafletRef.current.initPositionRef = null
          leafletRef.current.createPolylineRef = null
          return
        }

        // set the new temp link & remove initpos to prevent drag from map event
        leafletRef.current.createPolylineRef?.setLatLngs([
          leafletRef.current.initPositionRef.latlng,
          { lat, lng }
        ])

        const currentId = leafletRef.current.initPositionRef.id

        const point1Hgap =
          points.find((item) => item.id === currentId)?.initView?.radarHGap || 0

        if (!currentId) {
          return
        }

        const h1 =
          Krpano.GetAngle(
            lat - leafletRef.current.initPositionRef.latlng.lat,
            lng - leafletRef.current.initPositionRef.latlng.lng
          ) - point1Hgap

        const h2 =
          Krpano.GetAngle(
            leafletRef.current.initPositionRef.latlng.lat - lat,
            leafletRef.current.initPositionRef.latlng.lng - lng
          ) - hgap

        leafletRef.current.initPositionRef = null
        const existedLink = links.find(
          (item) =>
            item &&
            item.id &&
            typeof item.id === 'string' &&
            item.id.includes(currentId) &&
            item.id.includes(id)
        )
        if (!existedLink) {
          handlePoint(
            {
              query: ['create-link'],
              data: {
                building_id: Number(buildingId),
                floor_id: Number(floorId),
                h1: Krpano.reConfigAngleOver360(h1),
                h2: Krpano.reConfigAngleOver360(h2),
                point_id1: currentId,
                point_id2: id
              }
            },
            {
              onSuccess: () => {
                const point1 = points.find(
                  (item) => item && item.id === currentId
                )
                if (!point1) {
                  return
                }

                setLinks((prev) => [
                  ...prev,
                  {
                    id:
                      currentId > id
                        ? `${currentId}_${id}`
                        : `${id}_${currentId}`,
                    point1: {
                      id: currentId,
                      x: point1?.x || 0,
                      y: point1?.y || 0
                    },
                    point2: {
                      id,
                      x: lng,
                      y: lat
                    }
                  }
                ])
                leafletRef.current.mapContainerRef.removeLayer(
                  leafletRef.current.createPolylineRef
                )
                leafletRef.current.createPolylineRef = null
                if (
                  currentId === currentSelectedPoint ||
                  id === currentSelectedPoint
                ) {
                  setRevalidatePointDetail(currentSelectedPoint)
                }
              },
              onError: (error) => {
                leafletRef.current.mapContainerRef.removeLayer(
                  leafletRef.current.createPolylineRef
                )
                leafletRef.current.createPolylineRef = null
                Utils.handleErrorNavigate(error, navigate)
              }
            }
          )
        } else {
          leafletRef.current.mapContainerRef.removeLayer(
            leafletRef.current.createPolylineRef
          )
          leafletRef.current.createPolylineRef = null
        }
      }
    }
  }

  const handleUpdatePointImageByDrag = (pointId: string, image360: number) => {
    !isMutating &&
      handlePoint(
        {
          method: 'patch',
          query: [pointId],
          data: {
            floor_id: +(floorId || 0),
            building_id: +(buildingId || 0),
            image360
          }
        },
        {
          onSuccess: () => {
            const newPointList = points.map((point) =>
              point.id === pointId
                ? {
                    ...point,
                    image360:
                      buildingFiles.files.find(
                        (file) => file.id === image360
                      ) || null
                  }
                : point
            )
            const newFileList = buildingFiles.files.map((file) =>
              file.id === image360 ? { ...file, pointId } : file
            )
            setPoints(newPointList)
            setBuildingFiles((prev) => ({
              ...prev,
              files: newFileList
            }))
            setRevalidatePointDetail(pointId)
          },
          onError: (err) => {
            if (err.response.data.error.key === 'not_change_image_360') {
              refetchPoints()
              setRevalidateFileList(1)
              notification.error({
                message: t('translation.imageIsUsed')
              })
            } else {
              Utils.handleErrorNavigate(err, navigate)
            }
          }
        }
      )
  }

  const handleUpdateOnDragPoint = useCallback(async () => {
    try {
      await syncChangedPoint({
        method: 'patch',
        params: {
          building_id: buildingId,
          floor_id: floorId
        },
        data: {
          changedPoints: listChangedPoints
        }
      })

      const updatingPoints = []
      const krpano = document.getElementById('embedpano-full') as any
      for (let i = 0; i < points.length; i += 1) {
        const element = points[i]
        const hgap = element.initView.radarHGap

        const changedPoint = listChangedPoints.find(
          (item) => item.id === element.id
        )

        const updatingDirs: { id: string; h: number; v: number }[] = []

        const updateAbleDirections = element.directions?.filter(
          (item) => !item.dhgap
        )

        if (changedPoint) {
          const dirs = updateAbleDirections?.map((dir) => {
            const pointToChanged = listChangedPoints.find(
              (item) => item.id === dir.point_to
            )
            if (pointToChanged) {
              const h =
                Krpano.GetAngle(
                  pointToChanged.y - changedPoint.y,
                  pointToChanged.x - changedPoint.x
                ) -
                2 * (hgap > 180 ? 0 : hgap)
              updatingDirs.push({ id: dir.id, h, v: dir.v })
              return { ...dir, h }
            }
            const pointToOriginal = points.find(
              (item) => item.id === dir.point_to
            )
            const h = pointToOriginal
              ? Krpano.GetAngle(
                  pointToOriginal.y - changedPoint.y,
                  pointToOriginal.x - changedPoint.x
                ) -
                2 * (hgap > 180 ? 0 : hgap)
              : dir.h
            updatingDirs.push({ id: dir.id, h, v: dir.v })
            return { ...dir, h }
          })
          if (element.id === pointDetail?.id) {
            setPointDetail((prev) => prev && { ...prev, directions: dirs })
          }
        } else {
          const dirs = updateAbleDirections?.map((dir) => {
            const pointTo = listChangedPoints.find(
              (item) => item.id === dir.point_to
            )
            if (pointTo) {
              const h =
                Krpano.GetAngle(pointTo.y - element.y, pointTo.x - element.x) -
                2 * (hgap > 180 ? 0 : hgap)
              updatingDirs.push({ id: dir.id, h, v: dir.v })
              return { ...dir, h }
            }
            return dir
          })
          if (element.id === pointDetail?.id) {
            setPointDetail((prev) => prev && { ...prev, directions: dirs })
          }
        }

        if (updatingDirs.length) {
          updatingPoints.push({
            id: element.id,
            directions: updatingDirs
          })
          if (element.id === pointDetail?.id) {
            updatingDirs.forEach((item) => {
              krpano?.call(`
                set(hotspot['arrow-${item.id}'].ath, ${item.h});
                set(hotspot['arrow-${item.id}'].atv, ${item.v});
                set(hotspot['arrow-tooltip-${item.id}'].ath, ${item.h});
                set(hotspot['arrow-tooltip-${item.id}'].atv, ${item.v});
              `)
            })
          }
        }
      }

      if (updatingPoints.length && buildingId && floorId) {
        handleUpdateDirections({
          method: 'put',
          data: {
            building_id: +buildingId,
            floor_id: +floorId,
            point_directions: updatingPoints
          }
        })
      }
      setListChangedPoints([])
      debouncedRef.current = null
    } catch (error) {
      Utils.handleErrorNavigate(error, navigate)
    }
  }, [
    buildingId,
    floorId,
    handleUpdateDirections,
    listChangedPoints,
    navigate,
    pointDetail?.id,
    points,
    setListChangedPoints,
    setPointDetail,
    syncChangedPoint
  ])

  useEffect(() => {
    if (listChangedPoints.length && !debouncedRef.current) {
      debouncedRef.current = setTimeout(() => {
        handleUpdateOnDragPoint()
      }, 1000)
    }
  }, [handleUpdateOnDragPoint, listChangedPoints.length])

  return {
    isSyncChangedPoint,
    isFetchingPoint,
    linkEventHandler,
    markerEventHandler,
    handleUpdatePointImageByDrag
  }
}
